/**
 * useGsapHomepageFx - composite homepage animation hook.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 1.5 BOOST (2026-05-27).
 *
 * Bundle 4 hero-scoped effect vao 1 dynamic-import GSAP call (thay vi 4 hook
 * import rieng -> 4 lan parse cost):
 *
 *   FX1. Blob parallax - 3 ember blob drift scroll-scrubbed (y: -40px cap).
 *   FX2. Mouse-follow blob shift - blob respond cursor 8px max via quickTo.
 *   FX3. Headline word stagger - [data-word] drop-in rotateX 45deg.
 *   FX4. Scroll progress bar - fixed top 3px scaleX gradient track scroll.
 *
 * Note: CountUp + tile tilt + mouse halo deliberately o hook rieng vi:
 *   - CountUp = scope nho gon, ScrollTrigger once (xem use-countup-scroll.ts).
 *   - Tile tilt = re-use useGsapReveal pattern voi rotateY (xem use-gsap-tile-tilt.ts).
 *   - Mouse halo = pure CSS var update + rAF, KHONG can GSAP (xem use-mouse-halo.ts).
 *
 * Pattern:
 *  1. Single dynamic import('gsap') + import('gsap/ScrollTrigger') -> share
 *     across 4 fx.
 *  2. gsap.context() scope rooted o heroRef -> ctx.revert() on unmount
 *     cleanup all 4 fx.
 *  3. prefers-reduced-motion: reduce -> skip GSAP, fire onComplete (citation
 *     pulse fallback) - same contract as old useGsapHeroMount.
 *  4. Mobile (< 720px) cap blob parallax distance 20px (vs 40px desktop) per
 *     C10 motion sickness.
 *  5. Cleanup: removeEventListener mousemove + cancelAnimationFrame + ctx.revert.
 *
 * Spec: F-FE-THEME-GEMINI.md §6.5.2 (blob parallax row), §6.5.4 (transform-only),
 * §6.5.5 (reduced-motion).
 */

import { useEffect, useRef } from 'react';

export type HomepageFxOptions = {
  /** Fire khi word stagger timeline complete -> trigger citation pulse. */
  onComplete?: () => void;
  /** Blob CSS selector list. Default 3 .hero-ember-blob[data-blob-id]. */
  blobSelector?: string;
  /** Word stagger selector. Default '[data-word]'. */
  wordSelector?: string;
  /** Scroll progress bar selector. Default '#scroll-progress'. */
  progressSelector?: string;
};

const DEFAULTS = {
  blobSelector: '.hero-ember-blob',
  wordSelector: '.hero-ember-headline [data-word]',
  progressSelector: '#scroll-progress',
};

export function useGsapHomepageFx<T extends HTMLElement = HTMLElement>(
  options: HomepageFxOptions = {}
) {
  const ref = useRef<T | null>(null);
  const opts = { ...DEFAULTS, ...options };
  const onComplete = options.onComplete;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Reduced motion guard - skip GSAP, still fire onComplete cho citation
    // pulse CSS keyframe fallback.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (onComplete) onComplete();
      return;
    }

    if (!ref.current) return;

    const root = ref.current;
    const isMobile = window.matchMedia('(max-width: 720px)').matches;
    const blobYCap = isMobile ? 20 : 40; // per C10 motion sickness mitigation

    let revert: (() => void) | undefined;
    let removeMouseHandler: (() => void) | undefined;

    (async () => {
      try {
        const [{ gsap }, scrollTriggerMod] = await Promise.all([
          import('gsap'),
          import('gsap/ScrollTrigger'),
        ]);
        const ScrollTrigger = scrollTriggerMod.ScrollTrigger;
        gsap.registerPlugin(ScrollTrigger);

        const ctx = gsap.context(() => {
          /* ---------- FX3. Headline word stagger ------------- */
          // Word stagger first (mount-time, no ScrollTrigger).
          // Must run FIRST so onComplete fires for citation pulse chain.
          // Use timeline to chain block fade-in AFTER words (overlap 60% in).
          const tl = gsap.timeline({
            onComplete: () => {
              if (onComplete) onComplete();
            },
          });
          // Word-level (.word inside headline) - drop-in with rotateX.
          tl.from(opts.wordSelector, {
            opacity: 0,
            y: 24,
            rotateX: 45,
            transformOrigin: '50% 100%',
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.06,
          });
          // Block-level (lead + CTA + sublink + eyebrow). Overlap 70% into
          // word timeline for layered cascade feel.
          tl.from(
            '[data-hero-block]',
            {
              opacity: 0,
              y: 16,
              duration: 0.6,
              ease: 'power3.out',
              stagger: 0.08,
            },
            '-=0.35'
          );

          /* ---------- FX1. Blob parallax scrub --------------- */
          // 3 blob, different speed for depth. y0 set to 0; scrub map.
          const blobs = root.querySelectorAll<HTMLElement>(opts.blobSelector);
          blobs.forEach((blob, idx) => {
            // Speed ratio: blob 1 -1.0x, blob 2 -0.65x, blob 3 -1.25x.
            const speed = idx === 0 ? 1.0 : idx === 1 ? 0.65 : 1.25;
            gsap.to(blob, {
              y: -blobYCap * speed,
              ease: 'none',
              scrollTrigger: {
                trigger: root,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
              },
            });
          });

          /* ---------- FX4. Scroll progress bar ----------------- */
          // scaleX 0 -> 1 across entire document scroll height.
          const progressEl = document.querySelector<HTMLElement>(
            opts.progressSelector
          );
          if (progressEl) {
            gsap.set(progressEl, { scaleX: 0, transformOrigin: '0 0' });
            ScrollTrigger.create({
              start: 0,
              end: 'max',
              onUpdate: (self) => {
                progressEl.style.transform = `scaleX(${self.progress})`;
              },
            });
          }
        }, root);

        revert = () => ctx.revert();

        /* ---------- FX2. Mouse-follow blob shift (quickTo) ------- */
        // Skip on touch device (no hover).
        if (!window.matchMedia('(hover: none)').matches && !isMobile) {
          const blobs = root.querySelectorAll<HTMLElement>(opts.blobSelector);
          if (blobs.length > 0) {
            // Per-blob quickTo. quickTo cache prop setter -> ~200ms response.
            const xSetters = Array.from(blobs).map((b) =>
              gsap.quickTo(b, 'x', { duration: 0.4, ease: 'power2.out' })
            );
            const ySetters = Array.from(blobs).map((b) =>
              gsap.quickTo(b, 'y', {
                duration: 0.4,
                ease: 'power2.out',
              })
            );

            let raf = 0;
            let pendingX = 0;
            let pendingY = 0;
            const handler = (e: MouseEvent) => {
              const rect = root.getBoundingClientRect();
              // Normalize -1..1 from hero center.
              const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
              const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
              pendingX = nx;
              pendingY = ny;
              if (raf) return;
              raf = requestAnimationFrame(() => {
                raf = 0;
                blobs.forEach((_, i) => {
                  // Direction alternate per blob for organic feel.
                  const sign = i === 1 ? -1 : 1;
                  const xSetter = xSetters[i];
                  const ySetter = ySetters[i];
                  if (xSetter) xSetter(pendingX * 8 * sign);
                  if (ySetter) ySetter(pendingY * 8 * sign);
                });
              });
            };
            root.addEventListener('mousemove', handler, { passive: true });
            removeMouseHandler = () => {
              root.removeEventListener('mousemove', handler);
              if (raf) cancelAnimationFrame(raf);
            };
          }
        }
      } catch (e) {
        if (typeof console !== 'undefined') {
          console.warn('[useGsapHomepageFx] GSAP load failed:', e);
        }
        if (onComplete) onComplete();
      }
    })();

    return () => {
      removeMouseHandler?.();
      revert?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
}
